import { useOpennable } from 'hooks';
import { useCallback, useRef, useState } from 'react';
import { useSchedulingAnalytics } from '.';
import { defaultSchedulingColors } from '../constants/constants';
import { useSchedulingData } from '../context/SchedulingDataContext';
import { IScheduleReminderDetails, ISchedulingPopoverEvent, SchedulingEventType } from '../types/types';
import { getSchedulingColor, isSchedulingEventTimeOff } from '../utils/utils';
import useCalendarView from './useCalendarView';

function useSchedulingReadablePopover() {
  const popoverTimeout = useRef<NodeJS.Timeout>();
  const currentView = useCalendarView();
  const analytics = useSchedulingAnalytics();
  const { reminderDetails } = useSchedulingData();

  const [popoverEvent, setPopoverEvent] = useState<ISchedulingPopoverEvent>();
  const popoverDetails = useOpennable();

  function onEventClick(event: SchedulingEventType, mouseEvent: React.SyntheticEvent<HTMLElement>) {
    const element = mouseEvent.target as HTMLElement;
    const eventElement = element.closest('.rbc-event') ?? element.closest('.scheduling-grid-view-event');

    if (!eventElement) {
      return;
    }

    if (isSchedulingEventTimeOff(event)) {
      setPopoverEvent({
        event,
        color: event.color ?? defaultSchedulingColors.timeOff,
        target: eventElement.getBoundingClientRect(),
      });
      popoverDetails.open();
    } else {
      const item = findScheduleById(event.id);

      if (item) {
        analytics.recordPopoverAction(currentView, 'OPEN_SCHEDULE');
        const color = getSchedulingColor(event.type, item.reminder.color);
        setPopoverEvent({ event, color, target: eventElement.getBoundingClientRect() });
        popoverDetails.open();
      }
    }
  }

  function findScheduleById(id: string): { reminder: IScheduleReminderDetails; index: number } | null {
    const index = reminderDetails.findIndex((schedule) => schedule.id === id);
    if (index !== -1) {
      return { reminder: reminderDetails[index], index };
    } else {
      return null;
    }
  }

  const clearPopoverEvent = useCallback(() => {
    if (popoverDetails.isOpen) {
      popoverDetails.close();

      if (popoverTimeout.current) {
        clearTimeout(popoverTimeout.current);
      }

      popoverTimeout.current = setTimeout(() => {
        setPopoverEvent(undefined);
      }, 250);
    } else {
      setPopoverEvent(undefined);
    }
  }, [popoverDetails]);

  return { onEventClick, clearPopoverEvent, popoverEvent, popoverDetails };
}

export default useSchedulingReadablePopover;
